import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState, Children } from 'react'
import Equalizer from 'react-equalizer'
import Flickity from 'react-flickity-component'
import { useFlickityVisibilityFix } from '../../hooks/UseFlickityVisibilityFix'
import { FlickityRefObject } from '../../types/FlickityRefObject'
import {
  StyledCarouselContainer,
  StyledFlickityComponent,
  StyledFlickityNavigation,
  StyledNavigationContainer,
} from './styledCardDesignCarousel'
import { faArrowLeft, faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { Icon } from '../icon/icon'
import styled from 'styled-components'
import { Typography } from '@material-ui/core'
import { rem } from '../../utils/rem'

export type CardDesignCarouselProps = Omit<React.ComponentProps<typeof Flickity>, 'flickityRef' | 'title'> & {
  columns?: 2 | 3 | 4
  $overflow?: boolean
  title?: React.ReactNode
}

export const ButtonIcon = styled(Icon)`
  height: ${props => props.theme.typography.pxToRem(18)};
  width: ${props => props.theme.typography.pxToRem(20)};
  fill: ${props => props.theme.palette.common.black};
  background: none;

  &:hover {
    fill: ${props => props.theme.palette.secondary.dark};
  }
`

const StyledHeading = styled(Typography)`
  font-weight: ${props => props.theme.typography.fontWeightBold};
  letter-spacing: ${props => rem(1.5, props.theme)};
  color: ${props => props.theme.palette.primary.dark};
  text-transform: uppercase;
  opacity: 1;
  text-align: center;
  margin-bottom: 0;

  ${props => props.theme.breakpoints.up('sm')} {
    text-align: left;
  }
`

export const CardDesignCarousel: React.FC<PropsWithChildren<CardDesignCarouselProps>> = ({ children, columns = 2, title, $overflow, ...props }) => {
  const flickityRef = useRef(null) as FlickityRefObject
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const isTablet = useMediaQuery(theme.breakpoints.down('sm'))
  const totalItems = React.Children.toArray(children).length
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isReady, setIsReady] = useState(false)

  useFlickityVisibilityFix(flickityRef)

  if (totalItems === 0) {
    return null
  }

  const groupCells = useMemo(() => {
    return isMobile ? 1 : (isTablet ? 2 : columns)
  }, [isMobile, isTablet, columns])

  useEffect(() => {
    if (flickityRef.current?.flkty && flickityRef.current.flkty?.options.groupCells !== groupCells) {
      flickityRef.current.flkty.options.groupCells = groupCells
      flickityRef.current.flkty.resize()
    }
  }, [flickityRef, groupCells])

  const getNodes = useCallback((component: React.ComponentType, node: Element) => {
    return node.querySelectorAll('[data-same-height]')
  }, [])

  const goNext = () => {
    flickityRef.current?.flkty?.next()
  }

  const goPrev = () => {
    flickityRef.current?.flkty?.previous()
  }


  useEffect(() => {
    const flickityInstance = flickityRef.current?.flkty

    if (flickityInstance) {
      setIsReady(true)

      // Update selected index on slide change
      const updateSelected = () => setSelectedIndex(flickityInstance.selectedIndex)

      flickityInstance.on('select', updateSelected)

      // Set initial value
      setSelectedIndex(flickityInstance.selectedIndex)

      return () => {
        flickityInstance.off('select', updateSelected)
      }
    }
  }, [])

  return (
    <>
      <StyledCarouselContainer data-columns={columns - ($overflow ? 0.5 : 0)} data-items={Children.count(children)} $columns={columns} $overflow={$overflow}>
        <Equalizer nodes={getNodes} byRow={false}>
          <StyledNavigationContainer>
            {title && (
              <StyledHeading variant="h5">
                {title}
              </StyledHeading>
            )}
            <StyledFlickityNavigation>
              <button onClick={goPrev}><ButtonIcon icon={faArrowLeft}></ButtonIcon></button>
              <button onClick={goNext}><ButtonIcon icon={faArrowRight}></ButtonIcon></button>
            </StyledFlickityNavigation>
          </StyledNavigationContainer>
          <StyledFlickityComponent {...props}
            ref={flickityRef}
            $columns={columns}
            $hasNavigation={totalItems > groupCells}
            options={{
              cellAlign: 'left',
              adaptiveHeight: true,
              freeScroll: false,
              prevNextButtons: false,
              pageDots: true,
              groupCells: false,
              contain: true,
            }}>
            {children}
          </StyledFlickityComponent>
        </Equalizer>
      </StyledCarouselContainer>
    </>

  )
}
